import React, { forwardRef, useState, useRef, useEffect, useMemo } from 'react';
import { Input, styled, Flex, Shape, Label } from '@teko/ds-foundation';
import { SearchOutlined, CheckOutlined, ForwardOutlined } from '@teko/ds-icon';
import { BottomSheet } from '../BottomSheet/BottomSheet.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const AutoComplete = forwardRef((_a, ref) => {
  var _b = _a, {
    searchPlaceholder,
    searchTitle,
    renderOption,
    options,
    value,
    onChange,
    label,
    required,
    error,
    disabled
  } = _b, props = __objRest(_b, [
    "searchPlaceholder",
    "searchTitle",
    "renderOption",
    "options",
    "value",
    "onChange",
    "label",
    "required",
    "error",
    "disabled"
  ]);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef(null);
  const onChangeValue = (option) => {
    if (inputRef && (inputRef == null ? void 0 : inputRef.current)) {
      inputRef.current.value = (option == null ? void 0 : option.label) || "";
    }
  };
  useEffect(() => {
    onChangeValue(value);
  }, [value]);
  const onSelectChange = (option) => {
    onChangeValue(option);
    onChange(option);
    setShowBottomSheet(false);
    setKeyword("");
  };
  const filterOptions = useMemo(() => {
    if (!keyword || keyword === "")
      return options;
    return options.filter((option) => {
      return option.label.toLowerCase().includes(keyword.toLowerCase());
    });
  }, [keyword, JSON.stringify(options)]);
  const closeBottomSheet = () => {
    setShowBottomSheet(false);
    setKeyword("");
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, label && /* @__PURE__ */ React.createElement(StyledLabel, {
    className: "input__label",
    required,
    requiredPosition: "left"
  }, label), /* @__PURE__ */ React.createElement(Trigger, __spreadProps(__spreadValues({
    className: "select-trigger",
    type: "text",
    ref: inputRef
  }, props), {
    disabled,
    onClick: () => !disabled && setShowBottomSheet(true),
    readonly: true,
    suffix: /* @__PURE__ */ React.createElement(ForwardOutlined, null)
  })), !!error && /* @__PURE__ */ React.createElement(Error2, {
    className: "input__error"
  }, error), /* @__PURE__ */ React.createElement("input", {
    hidden: true,
    ref
  }), /* @__PURE__ */ React.createElement(StyledBottomSheet, {
    contentHeight: "75vh",
    show: showBottomSheet,
    closeBottomSheet
  }, /* @__PURE__ */ React.createElement(Header, {
    justify: "center",
    padding: ["s16", "s8"]
  }, searchTitle), /* @__PURE__ */ React.createElement(SearchWrapper, {
    padding: "s16"
  }, /* @__PURE__ */ React.createElement(Input, {
    size: "s",
    type: "text",
    prefix: /* @__PURE__ */ React.createElement(SearchOutlined, null),
    value: keyword,
    placeholder: searchPlaceholder,
    onChange: (e) => setKeyword(e)
  })), /* @__PURE__ */ React.createElement(List, null, filterOptions.map((option) => /* @__PURE__ */ React.createElement(ListItem, {
    key: option.value,
    justify: "space-between",
    padding: ["s12", "s16"],
    onClick: () => onSelectChange(option),
    active: (value == null ? void 0 : value.value) === option.value
  }, /* @__PURE__ */ React.createElement(WrapperItem, null, renderOption ? renderOption(option) : option.label), (value == null ? void 0 : value.value) === option.value && /* @__PURE__ */ React.createElement(CheckedWrapper, null, /* @__PURE__ */ React.createElement(CheckOutlined, {
    size: 24
  })))))));
});
const Trigger = styled(Input)`
  .input-container {
    background-color: unset;
    &:focus,
    &:focus-within {
      border-color: ${({ theme }) => theme.colors.divider} !important;
    }
  }
`;
const StyledBottomSheet = styled(BottomSheet)`
  width: 100%;
  .bottom-sheet-content {
    display: flex;
    flex-direction: column;
  }
`;
const Header = styled(Flex)`
  font-weight: 500;
  font-size: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;
const SearchWrapper = styled(Shape)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;
const List = styled(Shape)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
`;
const ListItem = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;
const WrapperItem = styled(Shape)`
  flex-grow: 1;
`;
const CheckedWrapper = styled.span`
  min-width: 24px;
`;
const StyledLabel = styled(Label)`
  margin-bottom: 0.4rem;
  font-weight: 500;
`;
AutoComplete.displayName = "AutoComplete";
const Error2 = styled.div(({ theme }) => `
    padding-top: 0;
    color: ${theme == null ? void 0 : theme.colors.error500};
    font-size: 0.75rem;
    line-height: 1rem;
    transition: color 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
    margin-top: 0.5rem;
  `);

export { AutoComplete };
